import { useCallback, useId, useRef, useState } from 'react'
import { PageHead } from '@components/PageHead'
import { createSendEncrypted, createSendFileEncrypted } from '@features/send/services/sendFlow'
import { GeneratorPopup } from '@features/tools/components/GeneratorPopup'
import { type SendItem, type SendType } from '@features/send/types'
import { sessionStore } from '@features/auth/services/sessionStore'
import { copyToClipboard, getEffectiveClipboardTtlSec } from '@services/clipboard'
import { FieldMarker } from '@shared/ui/FieldMarker'
import { Notice } from '@shared/ui/Notice'
import { PasswordInput } from '@shared/ui/PasswordInput'
import { useToast } from '@shared/ui/useToast'
import { Icon } from '@/icons/Icon'
import { useTranslation } from '@/i18n'

/**
 * Send 作成画面 (T-D-Send、design/js/screens-send.jsx SendCreate を移植)。
 *
 * 送信後は success state に切り替わり「リンクをコピー」ボタンを表示する。
 * Phase 4 §4.3 で file 送信に対応: type='file' 時は実 file picker + 10 MB 上限。
 */

const MAX_FILE_BYTES = 10 * 1024 * 1024

const formatBytes = (n: number): string => {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`

  return `${(n / (1024 * 1024)).toFixed(2)} MB`
}

type ExpiresOption = '1h' | '1d' | '7d' | '30d' | 'none'

const addHours = (h: number): string => {
  const d = new Date()

  d.setHours(d.getHours() + h)

  return d.toISOString()
}

const resolveExpiresAt = (opt: ExpiresOption): string | null => {
  if (opt === '1h') return addHours(1)
  if (opt === '1d') return addHours(24)
  if (opt === '7d') return addHours(24 * 7)
  if (opt === '30d') return addHours(24 * 30)

  return null
}

export const SendNewPage = (): JSX.Element => {
  const { t } = useTranslation()
  const { show: showToast } = useToast()

  const passwordFieldId = useId()
  const passwordHintFieldId = useId()
  const fileInputId = useId()
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const [type, setType] = useState<SendType>('text')
  const [name, setName] = useState('')
  const [content, setContent] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [maxAccess, setMaxAccess] = useState(1)
  const [expiresOpt, setExpiresOpt] = useState<ExpiresOption>('none')
  const [usePassword, setUsePassword] = useState(false)
  const [password, setPassword] = useState('')
  const [passwordHint, setPasswordHint] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [generatorOpen, setGeneratorOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [created, setCreated] = useState<SendItem | null>(null)

  const onFileSelected = useCallback(
    (selected: File | null): void => {
      setError(null)
      if (selected === null) {
        setFile(null)

        return
      }
      if (selected.size > MAX_FILE_BYTES) {
        setError(t('send.error.file_too_large', { max: formatBytes(MAX_FILE_BYTES) }))
        setFile(null)
        if (fileInputRef.current) fileInputRef.current.value = ''

        return
      }
      if (selected.size === 0) {
        setError(t('send.error.file_empty'))
        setFile(null)

        return
      }
      setFile(selected)
    },
    [t],
  )

  const onSubmit = useCallback(async (): Promise<void> => {
    setError(null)

    if (type === 'file' && file === null) {
      setError(t('send.error.file_required'))

      return
    }

    setIsSubmitting(true)

    const session = sessionStore.getState()
    if (session.kind !== 'unlocked') {
      setError(t('send.error.no_session'))
      setIsSubmitting(false)

      return
    }

    const cryptoOpts =
      usePassword && password ? { password, passwordHint: passwordHint || undefined } : {}

    try {
      const item =
        type === 'file' && file !== null
          ? await createSendFileEncrypted(session.symmetricKey, {
              file,
              name,
              maxAccess,
              expiresAt: resolveExpiresAt(expiresOpt),
              options: cryptoOpts,
            })
          : await createSendEncrypted(
              session.symmetricKey,
              {
                type: 'text',
                name: name.trim() || t('send.new.name_placeholder'),
                text: content,
              },
              maxAccess,
              resolveExpiresAt(expiresOpt),
              cryptoOpts,
            )

      setCreated(item)
      showToast({ titleKey: 'send.toast.created', tone: 'success' })
    } catch {
      setError(t('send.error.generic'))
    } finally {
      setIsSubmitting(false)
    }
  }, [
    type,
    file,
    name,
    content,
    maxAccess,
    expiresOpt,
    usePassword,
    password,
    passwordHint,
    t,
    showToast,
  ])

  const onCopyLink = useCallback(async (): Promise<void> => {
    if (!created) return
    try {
      await copyToClipboard(created.receiveUrl)
      showToast({
        titleKey: 'send.toast.link_copied',
        titleVars: { ttl: getEffectiveClipboardTtlSec() },
        tone: 'success',
        dedupe: true,
      })
    } catch {
      showToast({ titleKey: 'send.toast.copy_failed', tone: 'danger', dedupe: true })
    }
  }, [created, showToast])

  if (created) {
    return (
      <div>
        <PageHead
          title={t('send.new.success_title')}
          sub={t('send.new.success_subtitle')}
          actions={
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => {
                window.location.hash = 'send'
              }}
            >
              {t('send.new.back_to_list')}
            </button>
          }
        />
        <div className="page-body">
          <div className="card send-success-card">
            <div className="send-success-icon" aria-hidden="true">
              <Icon name="link" />
            </div>
            <div className="send-success-name nm">{created.payload.name}</div>
            <div className="send-success-url sb mono">{created.receiveUrl}</div>
            <button
              type="button"
              className="btn btn-primary send-success-copy"
              onClick={() => void onCopyLink()}
            >
              <Icon name="copy" aria-hidden="true" />
              {t('send.new.copy_link')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div>
      <PageHead
        title={t('send.new.title')}
        sub={t('send.new.subtitle')}
        actions={
          <>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => {
                window.location.hash = 'send'
              }}
            >
              {t('send.new.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={isSubmitting}
              onClick={() => void onSubmit()}
            >
              <Icon name="send" aria-hidden="true" />
              {t('send.new.submit')}
            </button>
          </>
        }
      />

      <div className="page-body">
        <div className="card send-create-card">
          {error && (
            <div className="send-create-error">
              <Notice tone="danger" ico="alert">
                {error}
              </Notice>
            </div>
          )}

          {/* タイプ選択 */}
          <div className="field">
            <div className="field-label">{t('send.new.type_label')}</div>
            <div className="send-type-picker">
              {(
                [
                  {
                    k: 'text' as SendType,
                    labelKey: 'send.new.type_text',
                    icon: 'file-text' as const,
                  },
                  {
                    k: 'file' as SendType,
                    labelKey: 'send.new.type_file',
                    icon: 'upload' as const,
                  },
                ] as const
              ).map(opt => (
                <button
                  key={opt.k}
                  type="button"
                  className={`send-type-btn btn btn-secondary${type === opt.k ? ' active' : ''}`}
                  onClick={() => setType(opt.k)}
                >
                  <Icon name={opt.icon} aria-hidden="true" />
                  <span>{t(opt.labelKey)}</span>
                </button>
              ))}
            </div>
          </div>

          {/* 名前 */}
          <div className="field">
            <label className="field-label" htmlFor="send-name">
              {t('send.new.name_label')}
            </label>
            <input
              id="send-name"
              className="input"
              type="text"
              value={name}
              onChange={ev => setName(ev.target.value)}
              placeholder={t('send.new.name_placeholder')}
            />
          </div>

          {/* コンテンツ / ファイル */}
          {type === 'text' ? (
            <div className="field">
              <label className="field-label" htmlFor="send-content">
                {t('send.new.content_label')}
              </label>
              <textarea
                id="send-content"
                className="textarea"
                rows={6}
                value={content}
                onChange={ev => setContent(ev.target.value)}
                placeholder={t('send.new.content_placeholder')}
              />
            </div>
          ) : (
            <div className="field">
              <label className="field-label" htmlFor={fileInputId}>
                {t('send.new.file_label')}
              </label>
              <label className={`send-file-drop${file ? ' selected' : ''}`} htmlFor={fileInputId}>
                <Icon name="upload" aria-hidden="true" />
                <div className="send-file-hint">
                  {file ? `${file.name} (${formatBytes(file.size)})` : t('send.new.file_hint')}
                </div>
                <div className="send-file-max">
                  {t('send.new.file_max_hint', { max: formatBytes(MAX_FILE_BYTES) })}
                </div>
                <input
                  ref={fileInputRef}
                  id={fileInputId}
                  type="file"
                  className="send-file-input"
                  onChange={ev => onFileSelected(ev.target.files?.[0] ?? null)}
                />
              </label>
              {file && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm send-file-clear"
                  onClick={() => {
                    setFile(null)
                    if (fileInputRef.current) fileInputRef.current.value = ''
                  }}
                >
                  {t('send.new.file_clear')}
                </button>
              )}
            </div>
          )}

          {/* パスワード保護 */}
          <div className="field">
            <label className="check-row" htmlFor="send-use-password">
              <input
                id="send-use-password"
                type="checkbox"
                checked={usePassword}
                onChange={ev => setUsePassword(ev.target.checked)}
              />
              <span>{t('send.new.password_toggle')}</span>
            </label>
          </div>

          {usePassword && (
            <>
              <div className="field">
                <label className="field-label" htmlFor={passwordFieldId}>
                  {t('send.new.password_label')}
                </label>
                <PasswordInput
                  id={passwordFieldId}
                  value={password}
                  onChange={ev => setPassword(ev.target.value)}
                  autoComplete="new-password"
                  visible={showPassword}
                  onVisibleChange={setShowPassword}
                  extraAction={
                    <button
                      type="button"
                      className="icon-btn sm"
                      onClick={() => setGeneratorOpen(true)}
                      aria-label={t('send.new.password_generate')}
                      title={t('send.new.password_generate')}
                      tabIndex={-1}
                    >
                      <Icon name="refresh" aria-hidden="true" />
                    </button>
                  }
                />
              </div>

              <div className="field">
                <label className="field-label" htmlFor={passwordHintFieldId}>
                  {t('send.new.password_hint_label')}
                  <FieldMarker />
                </label>
                <input
                  id={passwordHintFieldId}
                  className="input"
                  type="text"
                  value={passwordHint}
                  onChange={ev => setPasswordHint(ev.target.value)}
                  placeholder={t('send.new.password_hint_placeholder')}
                  maxLength={100}
                />
              </div>
            </>
          )}

          {/* アクセス数 / 有効期限 */}
          <div className="send-options-row">
            <div className="field">
              <label className="field-label" htmlFor="send-max-access">
                {t('send.new.max_access_label')}
              </label>
              <input
                id="send-max-access"
                className="input"
                type="number"
                min={1}
                value={maxAccess}
                onChange={ev => setMaxAccess(Math.max(1, parseInt(ev.target.value, 10) || 1))}
              />
            </div>
            <div className="field">
              <label className="field-label" htmlFor="send-expires">
                {t('send.new.expires_label')}
              </label>
              <select
                id="send-expires"
                className="select"
                value={expiresOpt}
                onChange={ev => setExpiresOpt(ev.target.value as ExpiresOption)}
              >
                {(
                  [
                    { k: '1h', labelKey: 'send.new.expires_1h' },
                    { k: '1d', labelKey: 'send.new.expires_1d' },
                    { k: '7d', labelKey: 'send.new.expires_7d' },
                    { k: '30d', labelKey: 'send.new.expires_30d' },
                    { k: 'none', labelKey: 'send.new.expires_none' },
                  ] as const
                ).map(opt => (
                  <option key={opt.k} value={opt.k}>
                    {t(opt.labelKey)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {generatorOpen && (
        <GeneratorPopup
          mode="password"
          onClose={() => setGeneratorOpen(false)}
          onUse={value => {
            setPassword(value)
            setShowPassword(true)
          }}
        />
      )}
    </div>
  )
}
